<template>
  <div>
    <!-- 商品明細コマンドツールチップ -->
    <BaseOperationTooltip
        :list="tooltipList"
        placement="bottom-end" />

    <!-- 数量入力ダイアログ -->
    <BaseDialog
        :title="dialogTitle"
        :visible.sync="isShownDialog"
        :show-only-close-button="false"
        :disable-ok-button="hasError"
        @closed="onDialogClosed"
        @cancel="isShownDialog = false"
        @ok="onDialogOk">
      <template slot="contents">
        <div class="dialog-content">
          <span> {{ dialogMessage }} </span>
          <BaseTableList v-if="dialogShowInputTable"
              :table-set-list="dialogSetLists"
              :display-items="dialog.dialogData"
              :editable="editable"
              :validate-scope="validateScope"
              class="qty-input-table">
            <template
                slot="targetFlag"
                slot-scope="{ row }">
              <el-checkbox v-model="row.row._$CCS_check" />
            </template>
          </BaseTableList>

          <!-- 対象明細セレクト -->
          <div v-if="dialogSelectOptions"
              class="target-detail-select">
            <div class="header">{{ dialogSelectLabel }}</div>
            <BaseValidSelect
                :options="dialogSelectOptions"
                :value="selectValue"
                popper-class="front-dialog"
                class="inputSelect"
                name="dialogSelect"
                size="mini"
                @input="onChange" />
          </div>
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.dialog-content {
  margin: 0 20px;
}
.qty-input-table >>> .el-table {
  box-shadow: initial;
}
.target-detail-select {
  display: flex;
}
.target-detail-select > * {
  padding: 5px;
}
div >>> .el-dialog {
  width: 40%;
  min-width: 600px;
}
.target-detail-select {
  display: flex;
}
.target-detail-select .inputSelect {
  flex: 1;
  min-width: calc(100% - 100px);
  width: 500px;
}
.target-detail-select .inputSelect >>> input {
  width: 100%;
}
</style>

<script>

import _ from 'lodash'
import { mapGetters } from 'vuex'

import common from '@/utils/common'
import math from '@/utils/math'
import * as define from '@/define'
import * as orderQuery from '@/queries/order'

import orderUtil from '@/components/mixins/orderUtil'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import BaseTableList from '@/components/molecules/BaseTableList'
import BaseValidSelect from '@/components/atoms/BaseValidSelect'
import BaseDialog from '@/components/molecules/BaseDialog'

/**
 * 削除可能か判定する
 * @param {*} target 判定対象
 * @returns {Boolean} trueの場合、削除可能
 */
const canRemove = (target) => {
  const find = _.chain(target)
    .get('qty')
    .pick([
      'ordered',
      'canceled',
      'inshipCanceled',
      'transfer',
      'receiveFixed',
      'exOut',
      'exOutExported',
      'exOutFixed',
      'exIn',
      'exInFixed'
    ])
    .find(val => Number(val) !== 0)
    .value()
  return _.get(target, 'uuid', '') === '' && find == null
}

export default {
  name: 'OrderDetailItemsToolTip',
  components: {
    BaseOperationTooltip,
    BaseTableList,
    BaseValidSelect,
    BaseDialog
  },
  mixins: [orderUtil],
  inject: ['parentValidator'],
  props: {
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    orderItem: {
      type: Object,
      default: () => {}
    },
    orderItems: {
      type: Array,
      default: () => {}
    },
    isItemRow: {
      type: Boolean,
      default: false
    },
    parentItem: {
      type: Object,
      default: () => {}
    },
    isBundleHeaderRow: {
      type: Boolean,
      default: false
    },
    isComponentRow: {
      type: Boolean,
      default: false
    },
    isOrderItemRow: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isShownDialog: false,
      selectValue: null,
      movement: {},
      dialog: {}
    }
  },
  computed: {
    define: () => define,
    ...mapGetters({
      wrappingCharge: 'wrappingCharge',
      shippingCharge: 'shippingCharge',
      codFee: 'codFee'
    }),
    ...mapGetters('OrderDetail', {
      siteCode: 'siteCode',
      taxInfo: 'taxInfo'
    }),
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    validateScope () {
      return 'OrderDetailItemsToolTip'
    },
    hasError () {
      return this.errors.any(this.validateScope)
    },
    editable () {
      return { qty: true }
    },
    tooltipList () {
      return [
        {
          id: 'unallocated',
          title: '未引当にする',
          show: this.isShowUnallocated,
          disabled: this.isDisabledUnallocated,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.pending > 1 && this.isBundleMoveable(this.orderItem, ['pending']),
          execute: () => this.unallocated(this.orderItem.qty)
        }, {
          id: 'preExport',
          title: '指図する',
          show: this.isShowPreExport,
          disabled: this.isDisabledPreExport,
          click: this.onTooltipItemClick,
          needShowDialog: () => {
            return math.add([
              this.orderItem.qty.unallocated,
              this.orderItem.qty.pending]) > 1 && this.isBundleMoveable(this.orderItem, ['unallocated', 'pending'])
          },
          execute: () => this.preExport(this.orderItem.qty)
        }, {
          id: 'exportCancel',
          title: '指図取消',
          show: this.isShowExportCancel,
          disabled: this.isDisabledPreExport,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.preExport > 1 && this.isBundleMoveable(this.orderItem, ['preExport']),
          execute: () => this.exportCancel(this.orderItem.qty)
        }, {
          id: 'canceled',
          title: 'キャンセル',
          show: this.isShowCancel,
          disabled: this.isDisabledCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => {
            return math.add([
              this.orderItem.qty.unallocated,
              this.orderItem.qty.pending,
              this.orderItem.qty.preExport,
              this.orderItem.qty.unshipped,
              this.orderItem.qty.preReExport]) > 1 &&
              this.isBundleMoveable(this.orderItem, ['unallocated', 'pending', 'preExport', 'unshipped', 'preReExport'])
          },
          execute: () => this.canceled(this.orderItem.qty)
        }, {
          id: 'canceledCancel',
          title: 'キャンセル取消',
          show: this.isShowCanceledCancel,
          disabled: this.isDisabledCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => {
            return math.add([
              this.orderItem.qty.canceled,
              this.orderItem.qty.inshipCanceled]) > 1 && this.isBundleMoveable(this.orderItem, ['canceled', 'inshipCanceled'])
          },
          execute: () => this.canceledCancel(this.orderItem.qty)
        }, {
          id: 'transfer',
          title: '商品振替',
          show: this.isShowTransfer,
          disabled: this.isDisabledTransfer,
          click: this.onTooltipItemClick,
          needShowDialog: () => true,
          execute: () => {}
        }, {
          id: 'shortage',
          title: '再送',
          show: this.isShowShortage,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.unshipped > 1,
          execute: () => this.shortage(this.orderItem.qty)
        }, {
          id: 'shortageExchange',
          title: '交換再送',
          show: this.isShowShortageExchange,
          click: this.onTooltipItemClick,
          needShowDialog: () => true,
          execute: () => {}
        }, {
          id: 'shortageCancel',
          title: '再送取消',
          show: this.isShowShortageCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.preReExport > 1,
          execute: () => this.shortageCancel(this.orderItem.qty)
        }, {
          id: 'shortageExchangeCancel',
          title: '交換再送取消',
          show: this.isShowShortageExchangeCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => {
            return _.chain(this.orderItem)
              .get('exchanges')
              .filter(val => val.isStockout === 1 && val.preExportQty > 0)
              .value().length > 1
          },
          execute: () => {
            _.chain(this.orderItem)
              .get('exchanges')
              .filter(val => val.isStockout === 1 && val.preExportQty > 0)
              .forEach(val => { val._$CCS_check = true })
              .value()
            this.shortageExchangeCancel(this.orderItem.qty)
          }
        }, {
          id: 'exchange',
          title: '交換',
          show: this.isShowExchange,
          click: this.onTooltipItemClick,
          needShowDialog: () => true,
          execute: () => {}
        }, {
          id: 'exchangeCancel',
          title: '交換取消',
          show: this.isShowExchangeCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => {
            return _.chain(this.orderItem)
              .get('exchanges')
              .filter(val => val.isStockout === 0 && (val.preExportQty > 0 || val.unshippedQty > 0))
              .value().length > 1
          },
          execute: () => {
            _.chain(this.orderItem)
              .get('exchanges')
              .filter(val => val.isStockout === 0 && (val.preExportQty > 0 || val.unshippedQty > 0))
              .forEach(val => { val._$CCS_check = true })
              .value()
            this.exchangeCancel(this.orderItem.qty)
          }
        }, {
          id: 'receive',
          title: '返品',
          show: this.isShowReceive,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.receiveShipped > 1,
          execute: () => this.receive(this.orderItem.qty)
        }, {
          id: 'receiveCancel',
          title: '返品取消',
          show: this.isShowReceiveCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.receiveFixed > 1,
          execute: () => this.receiveCancel(this.orderItem.qty)
        }, {
          id: 'forceReceive',
          title: '強制返品',
          show: this.isShowForceReceive,
          disabled: this.isDisabledForceReceive,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.shippedProcessableQty > 1,
          execute: () => this.forceReceive()
        }, {
          id: 'forceReceiveCancel',
          title: '強制返品取消',
          show: this.isShowForceReceiveCancel,
          disabled: this.isDisabledForceReceiveCancel,
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.receiveFixed > 1,
          execute: () => this.forceReceiveCancel()
        }, {
          id: 'addItemPriceAdjustment',
          title: '単品値引',
          show: this.isItemRow && this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ADD_ITEM_PRICE_ADJUSTMENT),
          disabled: this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ADD_ITEM_PRICE_ADJUSTMENT),
          click: this.onTooltipItemClick,
          needShowDialog: () => false,
          execute: () => this.$emit('addItemPriceAdjustment')
        }, {
          id: 'removeRow',
          title: '行削除',
          show: this.isShowRemoveRow,
          disabled: this.isDisabledRemoveRow,
          click: this.onTooltipItemClick,
          needShowDialog: () => false,
          execute: () => this.$emit('removeRow')
        }, {
          id: 'forceShipmentCanceled',
          title: '強制出荷取消',
          show: this.isShowForceShipmentCancel,
          disabled: this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FORCE_SHIPMENT_CANCELED),
          click: this.onTooltipItemClick,
          needShowDialog: () => this.orderItem.qty.shipped > 1,
          execute: () => this.forceShipmentCanceled(this.orderItem.qty)
        }
      ]
    },
    isShowUnallocated () {
      return this.mode === define.ORDER_MODE.EDIT && this.orderItem.orderItemType === 2 && _.get(this.orderItem, 'qty.pending', 0) > 0 &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.UNALLOCATED) && this.isShipment && !this.isComponentRow &&
      this.isBundleSameStatus(this.orderItem) && this.isBundleMoveable(this.orderItem, ['pending'])
    },
    isDisabledUnallocated () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.UNALLOCATED) && this.isShipment && !this.isComponentRow
    },
    isShowPreExport () {
      return this.mode === define.ORDER_MODE.EDIT &&
        (_.get(this.orderItem, 'qty.unallocated', 0) > 0 || _.get(this.orderItem, 'qty.pending', 0) > 0) &&
          this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.PRE_EXPORT) && this.isShipment &&
      this.isBundleSameStatus(this.orderItem) && this.isBundleMoveable(this.orderItem, ['unallocated', 'pending'])
    },
    isDisabledPreExport () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.PRE_EXPORT) && this.isShipment
    },
    isShowExportCancel () {
      return this.mode === define.ORDER_MODE.EDIT && _.get(this.orderItem, 'qty.preExport', 0) > 0 &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.PRE_EXPORT) && this.isShipment &&
      this.isBundleSameStatus(this.orderItem) && this.isBundleMoveable(this.orderItem, ['preExport'])
    },
    /** キャンセル */
    isShowCancel () {
      const isBundleCancel = this.isBundleHeaderRow
        // バンドルの場合
        ? !this.orderItem.bundleItems.some((val) => {
          // 非出荷商品 & 指図数、又は 出荷数 が 0 ではない & 数量 が 0 ではない明細の存在チェック
          return !val.isShipment &&
            ((this.$_orderUtil_exported(val.qty) !== 0 ||
                this.$_orderUtil_shipped(val.qty) !== 0) &&
              this.$_orderUtil_current(val.qty) !== 0)
        }) && this.isBundleSameStatus(this.orderItem)
        // バンドルではない場合
        : true

      return this.mode === define.ORDER_MODE.EDIT &&
        (_.get(this.orderItem, 'qty.unallocated', 0) > 0 ||
          _.get(this.orderItem, 'qty.pending', 0) > 0 ||
          _.get(this.orderItem, 'qty.preExport', 0) > 0 ||
          _.get(this.orderItem, 'qty.unshipped', 0) > 0 ||
          _.get(this.orderItem, 'qty.preReExport', 0) > 0) &&
        isBundleCancel &&
        !this.isComponentRow &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.CANCELED)
    },
    isDisabledCancel () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.CANCELED) && !this.isComponentRow
    },
    /** キャンセル取消 */
    isShowCanceledCancel () {
      return this.mode === define.ORDER_MODE.EDIT &&
        (_.get(this.orderItem, 'qty.canceled', 0) > 0 ||
          _.get(this.orderItem, 'qty.inshipCanceled', 0) > 0) &&
        !this.isComponentRow &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.CANCELED) &&
        this.isBundleSameStatus(this.orderItem) && this.isBundleMoveable(this.orderItem, ['canceled', 'inshipCanceled'])
    },
    isShowTransfer () {
      return this.mode === define.ORDER_MODE.EDIT &&
        (
          _.get(this.orderItem, 'qty.unallocated', 0) > 0 ||
          _.get(this.orderItem, 'qty.pending', 0) > 0 ||
          _.get(this.orderItem, 'qty.preExport', 0) > 0
        ) &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.TRANSFER) && !this.isComponentRow && !this.isBundleHeaderRow
    },
    isDisabledTransfer () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.TRANSFER) && !this.isComponentRow && !this.isBundleHeaderRow
    },
    isShowShortage () {
      return this.mode === define.ORDER_MODE.SHORTAGE && _.get(this.orderItem, 'qty.unshipped', 0) > 0 && !this.isComponentRow
    },
    isShowShortageExchange () {
      return this.mode === define.ORDER_MODE.SHORTAGE && _.get(this.orderItem, 'qty.unshipped', 0) > 0 &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.TRANSFER) && !this.isBundleHeaderRow && !this.isComponentRow
    },
    isShowShortageCancel () {
      const exchangePreExportQty = _.chain(this.orderItem)
        .get('exchanges')
        .filter(val => val.isStockout === 1 && val.preExportQty > 0)
        .map('preExportQty')
        .thru(val => math.add(val))
        .value()
      math.subtract([_.get(this.orderItem, 'qty.preReExport', 0), exchangePreExportQty])
      return this.mode === define.ORDER_MODE.SHORTAGE &&
        math.subtract([_.get(this.orderItem, 'qty.preReExport', 0), exchangePreExportQty]) > 0
    },
    isShowShortageExchangeCancel () {
      const exchanges = _.chain(this.orderItem)
        .get('exchanges')
        .filter(val => val.isStockout === 1 && val.preExportQty > 0)
        .value()
      return this.mode === define.ORDER_MODE.SHORTAGE && exchanges.length > 0 &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.TRANSFER) && !this.isComponentRow
    },
    isShowExchange () {
      return this.mode === define.ORDER_MODE.EXCHANGE &&
        this.shippedProcessableQty > 0 &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.TRANSFER) && !this.isBundleHeaderRow
    },
    isShowExchangeCancel () {
      return this.mode === define.ORDER_MODE.EXCHANGE &&
        (_.get(this.orderItem, 'qty.exOut', 0) > 0 || _.get(this.orderItem, 'qty.exOutUnshipped', 0) > 0) &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.TRANSFER) && !this.isBundleHeaderRow
    },
    /** 返品コマンド */
    isShowReceive () {
      return this.mode === define.ORDER_MODE.RECEIVED && _.get(this.orderItem, 'qty.receiveShipped', 0) > 0 &&
      !this.isComponentRow && this.isShipment &&
      !this.forceReceiveAble && !this.forceReceiveCancelAble
    },
    /** 返品取消コマンド */
    isShowReceiveCancel () {
      return this.mode === define.ORDER_MODE.RECEIVED && _.get(this.orderItem, 'qty.receiveFixed', 0) > 0 &&
      !this.isComponentRow && this.isShipment &&
      !this.forceReceiveAble && !this.forceReceiveCancelAble
    },
    /** 強制返品コマンド */
    isShowForceReceive () {
      return this.mode === define.ORDER_MODE.RECEIVED && this.forceReceiveAble && !this.isComponentRow &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FORCE_RECEIVE)
    },
    isDisabledForceReceive () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FORCE_RECEIVE)
    },
    /** 強制返品取消コマンド */
    isShowForceReceiveCancel () {
      return this.mode === define.ORDER_MODE.RECEIVED && this.forceReceiveCancelAble && !this.isComponentRow &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FORCE_RECEIVE_CANCEL)
    },
    isDisabledForceReceiveCancel () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FORCE_RECEIVE_CANCEL)
    },
    /** 強制出荷取消コマンド */
    isShowForceShipmentCancel () {
      return this.mode === define.ORDER_MODE.EDIT &&
        math.add([
          _.get(this.orderItem, 'qty.shipped', 0),
          -_.get(this.orderItem, 'qty.receiveFixed', 0),
          -_.get(this.orderItem, 'qty.exIn', 0),
          -_.get(this.orderItem, 'qty.exInFixed', 0)
        ]) > 0 &&
        this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FORCE_SHIPMENT_CANCELED) && !this.isComponentRow
    },
    /** 行削除コマンド */
    isShowRemoveRow () {
      // 商品行
      if (this.isOrderItemRow || this.isComponentRow) {
        return false
      }
      let funcId = ''
      // 手数料行（
      if (!this.isOrderItemRow && this.isItemRow) {
        switch (this.orderItem.productId) {
          case this.giftChargeProductId:
            funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.GIFT
            break
          case this.codFeeProductId:
            funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FEE
            break
          case this.shippingChargeProductId:
            funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.SHIPPING
            break
        }
      } else if (this.type === 'total') {
        // 小計後値引
        funcId = define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.MERCHANDISE_PRICE_ADJUSTMENTS
      } else {
        // 単品値引
        funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ADD_ITEM_PRICE_ADJUSTMENT
      }
      return this.$isFunctionShown(funcId)
    },
    isDisabledRemoveRow () {
      if (_.get(this.orderItem, 'uuid', '') !== '') {
        return true
      }

      let funcId = ''
      // 手数料行
      if (!this.isOrderItemRow && this.isItemRow) {
        switch (this.orderItem.productId) {
          case this.giftChargeProductId:
            funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.GIFT
            break
          case this.codFeeProductId:
            funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.FEE
            break
          case this.shippingChargeProductId:
            funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.SHIPPING
            break
        }
      } else if (this.type === 'total') {
        // 小計後値引
        funcId = define.PermissionKeys.Order.Detail.OrderInfo.TotalDetail.MERCHANDISE_PRICE_ADJUSTMENTS
      } else {
        // 単品値引
        funcId = define.PermissionKeys.Order.Detail.OrderInfo.ItemDetail.ADD_ITEM_PRICE_ADJUSTMENT
      }
      return this.$isFunctionDisabled(funcId)
    },
    codFeeProductId () {
      return this.codFee(this.siteCode).productId
    },
    giftChargeProductId () {
      return this.wrappingCharge(this.siteCode).productId
    },
    shippingChargeProductId () {
      return this.shippingCharge(this.siteCode).productId
    },
    dialogFactory () {
      return {
        unallocated: this.createUnallocatedDialog,
        preExport: this.createPreExportDialog,
        exportCancel: this.createExportCancelDialog,
        canceled: this.createCanceledDialog,
        canceledCancel: this.createCanceledCancelDialog,
        forceShipmentCanceled: this.createForceShipmentCanceledDialog,
        transfer: this.createTransferDialog,
        shortage: this.createShortageDialog,
        shortageExchange: this.createShortageExchangeDialog,
        shortageCancel: this.createShortageCancelDialog,
        shortageExchangeCancel: this.createShortageExchangeCancelDialog,
        exchange: this.createExchangeDialog,
        exchangeCancel: this.createExchangeCancelDialog,
        receive: this.createReceiveDialog,
        receiveCancel: this.createReceiveCancelDialog,
        forceReceive: this.createForceReceiveDialog,
        forceReceiveCancel: this.createForceReceiveCancelDialog
      }
    },
    dialogTitle () {
      return this.dialog.title
    },
    dialogMessage () {
      return this.dialog.message || '処理対象の数量を入力してください。'
    },
    dialogShowInputTable () {
      return this.dialog.isShownInputTable
    },
    dialogSetLists () {
      return this.dialog.setLists
    },
    dialogSelectOptions () {
      return this.dialog.selectOptions
    },
    dialogSelectLabel () {
      return this.dialog.selectLabel
    },
    /** 出荷有無フラグ */
    isShipment () {
      return _.get(this.orderItem, 'isShipment', false)
    },
    /** 出荷完了後処理可能数 */
    shippedProcessableQty () {
      return math.add([
        _.get(this.orderItem, 'qty.shipped', 0),
        -_.get(this.orderItem, 'qty.receiveFixed', 0),
        -_.get(this.orderItem, 'qty.exIn', 0),
        -_.get(this.orderItem, 'qty.exInFixed', 0),
        _.get(this.orderItem, 'qty.exOutFixed', 0)
      ])
    },
    /** 強制返品設定の取得 */
    forceReceiveSet () {
      return this.getServiceSiteConfig(null, 'order_detail', 'receive_config', 'force_receive').v[0]
    },
    /** 強制返品チェック */
    forceReceiveAble () {
      // 強制返品設定の取得
      if (this.forceReceiveSet === '0') {
        // 強制返品設定：0 のとき 操作不可
        return false
      } else if (this.forceReceiveSet === '1') {
        // 強制返品設定：1 のとき 出荷完了後処理可能数 > 0
        return this.shippedProcessableQty > 0
      } else if (this.forceReceiveSet === '2') {
        // 強制返品設定：2 のとき 出荷完了後処理可能数 > 0
        return this.isShipment && this.shippedProcessableQty > 0
      } else if (this.forceReceiveSet === '3') {
        // 強制返品設定：3 のとき 出荷完了後処理可能数 > 0
        return !this.isShipment && this.shippedProcessableQty > 0
      } else {
        return false
      }
    },
    /** 強制返品取消チェック */
    forceReceiveCancelAble () {
      if (this.forceReceiveSet === '0') {
        // 強制返品設定：0 のとき 操作不可
        return false
      } else if (this.forceReceiveSet === '1') {
        // 強制返品設定：1 のとき 処理済返品 > 0
        return _.get(this.orderItem, 'qty.receiveFixed', 0) > 0
      } else if (this.forceReceiveSet === '2') {
        // 強制返品設定：2 のとき 出荷商品 AND 処理済返品 > 0
        return this.isShipment && _.get(this.orderItem, 'qty.receiveFixed', 0) > 0
      } else if (this.forceReceiveSet === '3') {
        // 強制返品設定：3 のとき 非出荷商品 AND 処理済返品 > 0
        return !this.isShipment && _.get(this.orderItem, 'qty.receiveFixed', 0) > 0
      } else {
        return false
      }
    }
  },
  created () {
    this.$validator = this.parentValidator
    this.initialize()
    this.moveableInitialize()
    if (this.isBundleHeaderRow) {
      this.calculateBundleHeaderMoveableQty(this.orderItem)
    }
  },
  methods: {
    initialize () {
      // 初期化
      this.isShownDialog = false
      this.selectValue = null
      this.$set(this.movement, 'qty', {
        current: 0,
        ordered: 0,
        unallocated: 0,
        pending: 0,
        preExport: 0,
        preReExport: 0,
        canceled: 0,
        exported: 0,
        shipped: 0,
        unshipped: 0,
        inshipCanceled: 0,
        transfer: 0,
        receiveShipped: 0,
        receiveFixed: 0,
        exOut: 0,
        exOutExported: 0,
        exOutShipped: 0,
        exOutUnshipped: 0,
        exOutFixed: 0,
        exIn: 0,
        exInShipped: 0,
        exInFixed: 0
      })
      _.chain(this.orderItem)
        .get('exchanges')
        .forEach(val => {
          delete val._$CCS_check
        })
        .value()
      this.dialog = {
        title: '',
        execute: () => {},
        message: '',
        setLists: []
      }
    },
    moveableInitialize () {
      // 移動可能セット数初期化
      this.orderItem.moveableSetQty = Object.assign({},
        {
          current: 0,
          ordered: 0,
          unallocated: 0,
          pending: 0,
          preExport: 0,
          preReExport: 0,
          canceled: 0,
          exported: 0,
          shipped: 0,
          unshipped: 0,
          inshipCanceled: 0,
          transfer: 0,
          receiveShipped: 0,
          receiveFixed: 0,
          exOut: 0,
          exOutExported: 0,
          exOutShipped: 0,
          exOutUnshipped: 0,
          exOutFixed: 0,
          exIn: 0,
          exInShipped: 0,
          exInFixed: 0
        })
    },
    showDialog (command) {
      this.dialog = this.dialogFactory[command]()
      this.isShownDialog = true
    },
    createUnallocatedDialog () {
      return {
        title: '未引当にする',
        execute: () => this.unallocated(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '処理待ち', path: 'qty.pending',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.pending)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createPreExportDialog () {
      return {
        title: '指図する',
        execute: () => this.preExport(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '未引当', path: 'qty.unallocated',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.unallocated > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.unallocated)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '処理待ち', path: 'qty.pending',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.pending > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.pending)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createExportCancelDialog () {
      return {
        title: '指図取消',
        execute: () => this.exportCancel(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '指図待ち', path: 'qty.preExport',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.preExport)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createCanceledDialog () {
      return {
        title: 'キャンセル',
        execute: () => this.canceled(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '未引当', path: 'qty.unallocated',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.unallocated > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.unallocated)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '処理待ち', path: 'qty.pending',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.pending > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.pending)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '指図待ち', path: 'qty.preExport',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.preExport > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.preExport)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '出荷キャンセル', path: 'qty.unshipped',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.unshipped > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.unshipped)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '再送指図待ち', path: 'qty.preReExport',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.preReExport > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.preReExport)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createCanceledCancelDialog () {
      return {
        title: 'キャンセル取消',
        execute: () => this.canceledCancel(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '指図前キャンセル', path: 'qty.canceled',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.canceled > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.canceled)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '出荷前キャンセル', path: 'qty.inshipCanceled',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.inshipCanceled > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.inshipCanceled)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createTransferDialog () {
      const isShownInputTable = math.add([
        this.orderItem.qty.unallocated,
        this.orderItem.qty.pending,
        this.orderItem.qty.preExport]) > 1
      return {
        title: '商品振替',
        execute: () => {
          // 数量入力が非表示の場合、対象のカラムにセット
          if (!this.dialogShowInputTable) {
            this.movement.qty.unallocated = this.orderItem.qty.unallocated
            this.movement.qty.pending = this.orderItem.qty.pending
            this.movement.qty.preExport = this.orderItem.qty.preExport
          }
          this.transfer(this.movement.qty)
        },
        message: isShownInputTable ? '処理対象の数量の入力と振替先を選択して下さい。' : '振替先を選択して下さい。',
        isShownInputTable: isShownInputTable,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '未引当', path: 'qty.unallocated',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.unallocated > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.unallocated)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '処理待ち', path: 'qty.pending',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.pending > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.pending)}`,
            validateAs: '対象数量'
          },
          {
            type: 'Number', label: '指図待ち', path: 'qty.preExport',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            isShow: (column) => this.orderItem.qty.preExport > 0,
            validateRule: `max_value:${Number(this.orderItem.qty.preExport)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ],
        selectLabel: '振替先',
        selectOptions: this.createSelectOptions((val) => val.position !== this.orderItem.position && Boolean(val.isBundle) === false, 'new')
      }
    },
    createShortageDialog () {
      return {
        title: '再送',
        execute: () => this.shortage(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '出荷キャンセル', path: 'qty.unshipped',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.unshipped)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createShortageExchangeDialog () {
      const isShownInputTable = this.orderItem.qty.unshipped > 1
      return {
        title: '交換再送',
        execute: () => {
          // 数量入力が非表示の場合、対象のカラムにセット
          if (!this.dialogShowInputTable) {
            this.movement.qty.unshipped = this.orderItem.qty.unshipped
          }
          this.shortageExchange(this.movement.qty)
        },
        message: isShownInputTable ? '処理対象の数量の入力と交換先を選択して下さい。' : '交換先を選択して下さい。',
        isShownInputTable: isShownInputTable,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '出荷キャンセル', path: 'qty.unshipped',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `required|min_value:1|max_value:${Number(this.orderItem.qty.unshipped)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ],
        selectLabel: '交換先',
        selectOptions: this.createSelectOptions((val) => val.position !== this.orderItem.position && Boolean(val.isBundle) === false, 'new')
      }
    },
    createShortageCancelDialog () {
      return {
        title: '再送取消',
        execute: () => this.shortageCancel(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '再送指図待ち', path: 'qty.preReExport',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.preReExport)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createShortageExchangeCancelDialog () {
      return {
        title: '交換再送取消',
        execute: () => this.shortageExchangeCancel(),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'Slot', label: '', path: 'targetFlag', width: 50, class: 'center' },
          { type: 'String', label: '', path: 'srcOrderItem.position', width: 50 },
          { type: 'String', label: '交換元', path: 'srcOrderItem.productId', width: 200 },
          { type: 'String', label: '', path: 'srcOrderItem.lineitemText' },
          { type: 'String', label: '出荷指図ID', path: 'orderExport.id', width: 120 },
          { type: 'Number', label: '数量', path: 'preExportQty', width: 80 }
        ],
        dialogData: _.chain(this.orderItem)
          .get('exchanges')
          .filter(val => val.isStockout === 1 && val.preExportQty > 0)
          .value()
      }
    },
    createExchangeDialog () {
      // 交換可能数
      const exchangeable = this.shippedProcessableQty
      // 数量入力表示有無
      const isShownInputTable = this.shippedProcessableQty > 1

      return {
        title: '交換',
        execute: () => {
          // 数量入力が非表示の場合、対象のカラムにセット
          if (!this.dialogShowInputTable) {
            this.movement.qty.exchangeable = 1
          }
          this.exchange(this.movement.qty)
        },
        message: isShownInputTable ? '処理対象の数量の入力と交換先を選択して下さい。' : '交換先を選択して下さい。',
        isShownInputTable: isShownInputTable,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '交換可能数', path: 'qty.exchangeable',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `required|min_value:1|max_value:${Number(exchangeable)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: { exchangeable: exchangeable } },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ],
        selectLabel: '交換先',
        selectOptions: this.createSelectOptions((val) => Boolean(val.isBundle) === false, this.orderItem.position)
      }
    },
    createExchangeCancelDialog () {
      const exchanges = _.chain(this.orderItem)
        .get('exchanges')
        .filter(val => val.isStockout === 0 && (val.preExportQty > 0 || val.unshippedQty > 0))
        .value()
      return {
        title: '交換取消',
        execute: () => this.exchangeCancel(),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'Slot', label: '', path: 'targetFlag', width: 50, class: 'center' },
          { type: 'String', label: '', path: 'srcOrderItem.position', width: 50 },
          { type: 'String', label: '交換元', path: 'srcOrderItem.productId', width: 200 },
          { type: 'String', label: '', path: 'srcOrderItem.lineitemText' },
          { type: 'String', label: '出荷指図ID', path: 'orderExport.id', width: 120 },
          {
            type: 'Number', label: '数量', elementType: ELEMENT_TYPE.FUNCTION, width: 80,
            path: (row) => {
              if (row.preExportQty > 0) {
                return row.preExportQty
              } else if (row.unshippedQty > 0) {
                return row.unshippedQty
              }
            }
          }
        ],
        dialogData: exchanges
      }
    },
    createReceiveDialog () {
      return {
        title: '返品登録',
        execute: () => this.receive(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '返品', path: 'qty.receiveShipped',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.receiveShipped)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createReceiveCancelDialog () {
      return {
        title: '返品取消',
        execute: () => this.receiveCancel(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '処理済返品', path: 'qty.receiveFixed',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.receiveFixed)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createForceShipmentCanceledDialog () {
      return {
        title: '強制出荷取消',
        execute: () => this.forceShipmentCanceled(this.movement.qty),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '出荷取消可能数', path: 'qty.shipped',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.shipped) - Number(this.orderItem.qty.receiveFixed) -
              Number(this.orderItem.qty.exIn) - Number(this.orderItem.qty.exInFixed)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: { shipped: (Number(this.orderItem.qty.shipped) - Number(this.orderItem.qty.receiveFixed) -
            Number(this.orderItem.qty.exIn) - Number(this.orderItem.qty.exInFixed)) } },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createForceReceiveDialog () {
      // 処理可能数
      const processable = this.shippedProcessableQty

      return {
        title: '強制返品',
        execute: () => this.forceReceive(this.movement.qty.processable),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '処理可能数', path: 'qty.processable',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(processable)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: { processable: processable } },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },
    createForceReceiveCancelDialog () {
      return {
        title: '強制返品取消',
        execute: () => this.forceReceiveCancel(this.movement.qty.receiveFixed),
        message: '',
        isShownInputTable: true,
        setLists: [
          { type: 'String', label: '', path: 'label' },
          {
            type: 'Number', label: '処理済返品', path: 'qty.receiveFixed',
            elementType: ELEMENT_TYPE.INPUT_NUMBER, editableId: 'qty',
            editableCondition: (row) => row.id === 'movement',
            validateRule: `max_value:${Number(this.orderItem.qty.receiveFixed)}`,
            validateAs: '対象数量'
          }
        ],
        dialogData: [
          { id: 'current', label: '現在数量', qty: this.orderItem.qty },
          { id: 'movement', label: '対象数量', qty: this.movement.qty }
        ]
      }
    },

    /**
     * 振替や交換先のセレクトボックスの選択肢を生成する
     * @param filter 選択肢対象フィルター
     * @param selectValue 初期選択値
     */
    createSelectOptions (filter, selectValue) {
      this.selectValue = selectValue
      return _.chain(this.orderItems)
        .filter(val => filter == null ? true : filter(val))
        .map(val => ({
          code: val.position,
          name: `${this.isComponentRow ? `バンドル明細No${this.parentItem.position} - ` : ''}明細No${val.position} - ${val.lineitemText}`
        }))
        .push(({ code: 'new', name: '新規' }))
        .value()
    },

    onTooltipItemClick (item) {
      const needShowDialog = _.get(item, 'needShowDialog', () => {})()
      if (needShowDialog) {
        this.showDialog(item.id)
      } else {
        _.get(item, 'execute', () => {})()
        this.initialize()
      }
    },
    unallocated (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'pending' ], 'unallocated')
      this.updateComponentQty(qty, [ 'pending' ], 'unallocated', 'unallocated')
    },
    preExport (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'unallocated', 'pending' ], 'preExport')
      this.updateComponentQty(qty, [ 'unallocated', 'pending' ], 'preExport', 'preExport')
    },
    exportCancel (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'preExport' ], 'pending')
      this.updateComponentQty(qty, [ 'preExport' ], 'pending', 'exportCancel')
    },
    canceled (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'unallocated', 'pending', 'preExport' ], 'canceled')
      this.updateComponentQty(qty, [ 'unallocated', 'pending', 'preExport' ], 'canceled', 'canceled')
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'unshipped', 'preReExport' ], 'inshipCanceled')
      this.updateComponentQty(qty, [ 'unshipped', 'preReExport' ], 'inshipCanceled', 'canceled')
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },

    /** キャンセル取消 */
    canceledCancel (srcQty) {
      const qty = _.cloneDeep(srcQty)
      // 出荷前キャンセルの取消
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'inshipCanceled' ], 'unshipped')
      this.updateComponentQty(qty, [ 'inshipCanceled' ], 'unshipped', 'inshipCanceledCancel')

      // 指図前キャンセルの取消
      if (this.isShipment) {
        // 出荷商品の場合
        this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'canceled' ], 'pending')
        this.updateComponentQty(qty, [ 'canceled' ], 'pending', 'canceledCancel')
      } else {
        // 非出荷商品の場合
        this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'canceled' ], 'preExport')
      }

      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },

    /** 振替 */
    async transfer (srcQty) {
      const prev = {
        index: _.findIndex(this.orderItems, val => val.orderDetailItemsKey === this.orderItem.orderDetailItemsKey),
        item: _.cloneDeep(this.orderItem)
      }

      let targetItem = _.find(this.orderItems, { position: this.selectValue })
      let model

      if (targetItem == null) {
        model = await this.createModel('transfer')
      }
      _.forEach([ 'unallocated', 'pending', 'preExport' ], val => {
        // 移動数量の取得
        const moved = this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ val ], 'transfer')
        if (targetItem == null) {
          model.qty.transfer = math.subtract([ model.qty.transfer, moved ])
          model.qty[val] = moved
        } else {
          this.$_orderUtil_moveQty(targetItem.qty, { transfer: moved }, [ 'transfer' ], val)
        }
      })
      if (model != null) {
        this.$emit('addOrderItem', { model, prev })
      } else {
        targetItem.qty.current = this.$_orderUtil_calculateCurrentQty(targetItem.qty)
      }

      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
      if (canRemove(this.orderItem)) {
        this.$emit('removeRow')
      }
    },

    /** 再送 */
    shortage (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'unshipped' ], 'preReExport')
      this.updateComponentQty(qty, [ 'unshipped' ], 'preReExport')
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },

    /** 交換再送 */
    async shortageExchange (srcQty) {
      const prev = {
        index: _.findIndex(this.orderItems, val => val.orderDetailItemsKey === this.orderItem.orderDetailItemsKey),
        item: _.cloneDeep(this.orderItem)
      }

      // 移動数量の取得
      const moved = this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'unshipped' ], 'transfer')
      let targetItem = _.find(this.orderItems, { position: this.selectValue })

      if (targetItem == null) {
        const model = await this.createModel('shortageExchange')
        model.qty.transfer = -moved
        model.qty.preReExport = moved
        model.exchanges.push({
          srcOrderItem: {
            id: this.orderItem.id,
            auid: this.orderItem.auid,
            position: this.orderItem.position,
            lineitemText: this.orderItem.lineitemText,
            productId: this.orderItem.productId
          },
          destOrderItem: { position: model.position },
          quantity: moved,
          preExportQty: moved,
          isStockout: 1
        })
        this.$emit('addOrderItem', { model, prev })
      } else {
        this.$_orderUtil_moveQty(targetItem.qty, { transfer: moved }, [ 'transfer' ], 'preReExport')
        if (targetItem.exchanges == null) {
          targetItem.exchanges = []
        }
        targetItem.exchanges.push({
          srcOrderItem: {
            id: this.orderItem.id,
            auid: this.orderItem.auid,
            position: this.orderItem.position,
            lineitemText: this.orderItem.lineitemText,
            productId: this.orderItem.productId
          },
          quantity: moved,
          preExportQty: moved,
          isStockout: 1
        })
        targetItem.qty.current = this.$_orderUtil_calculateCurrentQty(targetItem.qty)
      }
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },
    shortageCancel (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'preReExport' ], 'unshipped')
      this.updateComponentQty(qty, [ 'preReExport' ], 'unshipped')
    },
    shortageExchangeCancel (srcQty) {
      const exchanges = _.chain(this.orderItem)
        .get('exchanges')
        .filter(val => val._$CCS_check)
        .value()
      exchanges.forEach(exchange => {
        const qty = exchange.preExportQty
        // 交換先数量修正
        exchange.quantity = math.subtract([exchange.quantity, qty])
        exchange.preExportQty = math.subtract([exchange.preExportQty, qty])
        this.$_orderUtil_moveQty(this.orderItem.qty, { preReExport: qty }, [ 'preReExport' ], 'transfer')
        this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)

        // 交換元数量修正
        const src = _.find(this.orderItems, val => val.position === exchange.srcOrderItem.position)
        if (src) {
          this.$_orderUtil_moveQty(src.qty, { transfer: qty }, [ 'transfer' ], 'unshipped')
          src.qty.current = this.$_orderUtil_calculateCurrentQty(src.qty)
        }
      })
      const index = _.findIndex(this.orderItem.exchanges, val => {
        return (val.uuid == null || val.uuid === '') && val.quantity === 0
      })
      if (index >= 0) {
        this.orderItem.exchanges.splice(index, 1)
      }
      if (canRemove(this.orderItem)) {
        this.$emit('removeRow')
      }
    },
    async exchange (srcQty) {
      const prev = {
        index: _.findIndex(this.orderItems, val => val.orderDetailItemsKey === this.orderItem.orderDetailItemsKey),
        item: _.cloneDeep(this.orderItem)
      }
      if (this.isComponentRow) prev.parent = this.parentItem

      // 移動数量の取得
      const moved = _.get(srcQty, 'exchangeable', 0)
      this.orderItem.qty.exIn = math.add([this.orderItem.qty.exIn, moved])
      let targetItem = _.find(this.orderItems, { position: this.selectValue })
      if (targetItem == null) {
        const model = await this.createModel('exchange')
        model.qty.exOut = moved
        model.exchanges.push({
          srcOrderItem: {
            id: this.orderItem.id,
            auid: this.orderItem.auid,
            position: this.orderItem.position,
            lineitemText: this.orderItem.lineitemText,
            productId: this.orderItem.productId
          },
          destOrderItem: { position: model.position },
          quantity: moved,
          preExportQty: moved,
          isStockout: 0
        })
        this.$emit('addOrderItem', { model, prev })
      } else {
        targetItem.qty.exOut = math.add([targetItem.qty.exOut, moved])
        if (targetItem.exchanges == null) {
          targetItem.exchanges = []
        }
        targetItem.exchanges.push({
          srcOrderItem: {
            id: this.orderItem.id,
            auid: this.orderItem.auid,
            position: this.orderItem.position,
            lineitemText: this.orderItem.lineitemText,
            productId: this.orderItem.productId
          },
          quantity: moved,
          preExportQty: moved,
          isStockout: 0
        })
        targetItem.qty.current = this.$_orderUtil_calculateCurrentQty(targetItem.qty)
      }
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
      this.calculateBundleHeaderItemQty()
    },
    exchangeCancel (srcQty) {
      const exchanges = _.chain(this.orderItem)
        .get('exchanges')
        .filter(val => val._$CCS_check)
        .value()
      exchanges.forEach(exchange => {
        if (exchange.preExportQty > 0) {
          this.exchangeCancelPreExport(exchange)
        } else if (exchange.unshippedQty > 0) {
          this.exchangeCancelUnshipped(exchange)
        }
      })
      const index = _.findIndex(this.orderItem.exchanges, val => {
        return (val.uuid == null || val.uuid === '') && val.quantity === 0
      })
      if (index >= 0) {
        this.orderItem.exchanges.splice(index, 1)
      }
      if (canRemove(this.orderItem)) {
        this.$emit('removeRow')
      }
      this.calculateBundleHeaderItemQty()
    },
    exchangeCancelPreExport (exchange) {
      const qty = exchange.preExportQty
      // 交換先数量修正
      exchange.quantity = math.subtract([exchange.quantity, qty])
      exchange.preExportQty = math.subtract([exchange.preExportQty, qty])
      this.orderItem.qty.exOut = math.subtract([this.orderItem.qty.exOut, qty])
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)

      // 交換元数量修正
      const src = _.find(this.orderItems, val => val.position === exchange.srcOrderItem.position)
      if (src) {
        src.qty.exIn = math.subtract([src.qty.exIn, qty])
        src.qty.current = this.$_orderUtil_calculateCurrentQty(src.qty)
      }
      this.calculateBundleHeaderItemQty()
    },

    /** 交換出荷不可キャンセル */
    exchangeCancelUnshipped (exchange) {

      // 移動数
      const qty = exchange.unshippedQty

      /** 交換情報数量 */
      exchange.unshippedQty = math.subtract([exchange.unshippedQty, qty])
      exchange.inshipCanceledQty = math.add([exchange.inshipCanceledQty, qty])

      /** 交換先数量 */
      this.orderItem.qty.exOutExported = math.subtract([this.orderItem.qty.exOutExported, qty])
      this.orderItem.qty.exOutUnshipped = math.subtract([this.orderItem.qty.exOutUnshipped, qty])
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)

      /** 交換元数量 */
      const src = _.find(this.orderItems, val => val.position === exchange.srcOrderItem.position)
      if (src) {
        src.qty.exIn = math.subtract([src.qty.exIn, qty])
        src.qty.current = this.$_orderUtil_calculateCurrentQty(src.qty)
      }

      // バンドルヘッダー再計算
      this.calculateBundleHeaderItemQty()

    },

    receive (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'receiveShipped' ], 'receiveFixed')
      this.updateComponentQty(qty, [ 'receiveShipped' ], 'receiveFixed', 'receive')
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },
    receiveCancel (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'receiveFixed' ], 'receiveShipped')
      this.updateComponentQty(qty, [ 'receiveFixed' ], 'receiveShipped', 'receiveCancel')
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },
    forceShipmentCanceled (srcQty) {
      const qty = _.cloneDeep(srcQty)
      this.$_orderUtil_moveQty(this.orderItem.qty, srcQty, [ 'shipped' ], 'preExport')
      this.updateComponentQty(qty, [ 'shipped' ], 'preExport', 'forceShipmentCanceled')
    },
    /** 強制返品処理 */
    forceReceive (moveQty) {
      const qty = moveQty != null ? moveQty : 1
      this.orderItem.qty.receiveFixed = math.add([this.orderItem.qty.receiveFixed, qty])
      const srcQty = { receiveFixed: qty }
      this.updateComponentQty(srcQty, [ 'receiveFixed' ], 'receiveFixed', 'forceReceive')
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },
    /** 強制返品取消処理 */
    forceReceiveCancel (moveQty) {
      const qty = moveQty != null ? moveQty : 1
      this.orderItem.qty.receiveFixed = math.subtract([this.orderItem.qty.receiveFixed, qty])
      const srcQty = { receiveFixed: qty }
      this.updateComponentQty(srcQty, [ 'receiveFixed' ], 'receiveFixed', 'forceReceiveCancel')
      this.orderItem.qty.current = this.$_orderUtil_calculateCurrentQty(this.orderItem.qty)
    },
    /* バンドルヘッダーの数量変更を構成品に反映する
     * @param {*} srcQty 移動数量(移動元)
     *  - 処理待ちから未引当に2点移動する場合、{ pending: 2 } が入る
     * @param {Array} preQtyKeys 移動元数量のkey配列
     * @param {String} postQtyKey 移動先数量のkey
     * @param {String} command 操作
     */
    updateComponentQty (srcQty, preQtyKeys, postQtyKey, command = null) {

      // バンドルヘッダーの場合
      if (this.isBundleHeaderRow) {

        // 構成品明細を展開
        this.orderItem.bundleItems.forEach((component) => {

          const isShipment = component.isShipment // 出荷有無
          /** 初期化 */
          let changePostKey = ''  // 移動先変更
          let changePreKey = ''  // 移動元変更

          // 非出荷商品
          if (!isShipment) {
            // 「未引当にする、指図する、指図取消、強制出荷取消、返品、返品取消」では移動しない
            if (['unallocated', 'preExport', 'exportCancel', 'forceShipmentCanceled',
              'receive', 'receiveCancel', 'forceReceive', 'forceReceiveCancel'].includes(command)) return
            // 「キャンセル取消」の場合、移動先を「指図待ち」にする
            if (['canceledCancel', 'inshipCanceledCancel'].includes(command)) {
              changePostKey = 'preExport'
            }
            // 「キャンセル」の場合、移動元を「指図待ち」にする
            if (['canceled'].includes(command)) {
              changePreKey = 'preExport'
            }
          }

          // 構成数量(1ユニット単位)の算出 (構成品.初回注文数 - 構成品振替数) / バンドルヘッダー.初回注文数
          const baseQty = math.divide([math.subtract([component.qty.ordered, component.qty.transfer]), this.orderItem.qty.ordered])

          if (['forceReceive', 'forceReceiveCancel'].includes(command)) {
            // 移動元変更キーがある場合、キーを取得
            const key = preQtyKeys[0]
            // バンドルヘッダーの移動数量を取得
            const changeQtyNum = srcQty[key]
            // 構成品に移動可能数があるかチェック
            const canMoveQty = +component.qty[key] >= baseQty || command === 'forceReceive'
            // ヘッダーの移動数量と構成数量を乗算して、移動数量を形成。移動可能数がない場合は 0 を設定
            const qty = canMoveQty ? math.multiply([changeQtyNum, baseQty]) : 0
            // 結果がマイナスになった場合、エラーを表示
            if (+component.qty[key] - qty < 0 && command !== 'forceReceive') {
              this.$notify.error(`数量移動処理で異常が発生しました。
              構成品の数量が正しいくない可能性があるため、保存処理は行わず、管理者に連絡してください。`)
            }
            // 処理済返品数の計算を行う
            if (command === 'forceReceive') {
              // 強制返品の場合、処理済返品数の加算を行う
              component.qty[key] = math.add([component.qty[key], qty])
            } else if (command === 'forceReceiveCancel') {
              // 強制返品取消の場合、処理済返品数の減算を行う
              component.qty[key] = math.subtract([component.qty[key], qty])
            }
          } else {
            // 移動先変更がある場合、キーを入れ替える
            const argPostQtyKey = (changePostKey || '') === '' ? postQtyKey : changePostKey
            const argPreQtyKeys = (changePreKey || '') === '' ? preQtyKeys : [ changePreKey ]

            const changeQty = {}
            // 移動元数量のkeyを展開
            preQtyKeys.forEach((preQtyKey) => {
              // バンドルヘッダーの移動数量を取得
              const changeQtyNum = srcQty[preQtyKey]
              // 移動元変更キーがある場合、キーを入れ替える
              const key = changePreKey || preQtyKey
              // 構成品に移動可能数があるかチェック
              const canMoveQty = +component.qty[key] >= baseQty
              // ヘッダーの移動数量と構成数量を乗算して、移動数量を形成。移動可能数がない場合は 0 を設定
              const qty = canMoveQty ? math.multiply([changeQtyNum, baseQty]) : 0
              // 結果がマイナスになった場合、エラーを表示
              if (+component.qty[key] - qty < 0) {
                this.$notify.error(`数量移動処理で異常が発生しました。
                構成品の数量が正しいくない可能性があるため、保存処理は行わず、管理者に連絡してください。`)
              }
              // 既に移動元のキーが存在する場合は加算
              changeQty[key] = (changeQty[key] || '') === '' ? qty : +changeQty[key] + qty
            })
            // 数量更新
            this.$_orderUtil_moveQty(component.qty, changeQty, argPreQtyKeys, argPostQtyKey)
            this.orderItem.moveableSetQty = Object.assign({}, this.orderItem.qty)
          }
          component.qty.current = this.$_orderUtil_calculateCurrentQty(component.qty)
        })

      } else if (this.isComponentRow) {
        // バンドル構成品の場合、バンドルヘッダー移動可能数を再計算
        this.calculateBundleHeaderMoveableQty(this.parentItem)
      }
    },
    // 構成品の情報からバンドルヘッダーの数量を算出（交換系の情報のみ）
    calculateBundleHeaderItemQty () {
      const calculate = (headerItem) => {
        const item = {
          exOut: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exOut)
            .reduce((sum, item) => math.add([sum, item])),
          exOutExported: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exOutExported)
            .reduce((sum, item) => math.add([sum, item])),
          exOutShipped: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exOutShipped)
            .reduce((sum, item) => math.add([sum, item])),
          exOutUnshipped: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exOutUnshipped)
            .reduce((sum, item) => math.add([sum, item])),
          exOutFixed: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exOutFixed)
            .reduce((sum, item) => math.add([sum, item])),
          exIn: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exIn)
            .reduce((sum, item) => math.add([sum, item])),
          exInShipped: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exInShipped)
            .reduce((sum, item) => math.add([sum, item])),
          exInFixed: _.map(headerItem.bundleItems, (componentItem) => componentItem.qty.exInFixed)
            .reduce((sum, item) => math.add([sum, item]))
        }

        headerItem.qty.exOut = item.exOut
        headerItem.qty.exOutExported = item.exOutExported
        headerItem.qty.exOutShipped = item.exOutShipped
        headerItem.qty.exOutUnshipped = item.exOutUnshipped
        headerItem.qty.exOutFixed = item.exOutFixed
        headerItem.qty.exIn = item.exIn
        headerItem.qty.exInShipped = item.exInShipped
        headerItem.qty.exInFixed = item.exInFixed
      }
      if (this.isComponentRow) {
        calculate(this.parentItem)
      } else if (this.isBundleHeaderRow) {
        calculate(this.orderItem)
      }
    },
    // バンドル商品移動可能数を計算
    calculateBundleHeaderMoveableQty (orderItem = null) {
      if (orderItem == null) orderItem = this.orderItem
      if (orderItem.isBundle) {
        const moveableSetQty = Object.assign({}, orderItem.qty)
        let minQty = Object.assign({}, orderItem.bundleItems[0].qty)
        const sumQty = Object.assign({}, orderItem.bundleItems[0].qty)
        let unitQty = 0
        orderItem.bundleItems.forEach((item, index) => {
          // 非出荷商品は対象外
          if (!item.isShipment) return
          // 構成数量(1ユニット単位)の算出 (構成品.初回注文数 - 構成品振替数) / バンドルヘッダー.初回注文数
          const baseQty = math.divide([math.subtract([item.qty.ordered, item.qty.transfer]), orderItem.qty.ordered])
          unitQty = unitQty + baseQty

          for (const qtyKey in item.qty) {
            if (qtyKey.slice(0, 2) === 'ex') continue
            // 交換系はスキップ
            if (index === 0) {
              // 初回に合計数量をクリア
              sumQty[qtyKey] = 0
              minQty[qtyKey] = []
            }
            // 移動可能数(構成数量単位)
            const moveableQty = item.qty[qtyKey] / baseQty
            sumQty[qtyKey] = sumQty[qtyKey] + Number(item.qty[qtyKey])
            minQty[qtyKey].push(moveableQty)

            // 移動可能セット数（1以上で移動可能）
            moveableSetQty[qtyKey] = Math.min(...minQty[qtyKey])
          }
        })
        // eslint-disable-next-line no-unused-vars
        for (const qtyKey in sumQty) {
          orderItem.qty[qtyKey] = sumQty[qtyKey] / unitQty
        }
        orderItem.moveableSetQty = Object.assign({}, moveableSetQty)
      }
    },
    onChange (v) {
      this.selectValue = v
    },
    onDialogClosed () {
      // ダイアログが閉じられた場合、初期化
      this.initialize()
    },

    /** 数量入力、移動先指定ダイアログ OK ボタン */
    async onDialogOk () {
      await this.$validator.validate()
      if (this.hasError) {
        return
      }
      this.dialog.execute()
      this.isShownDialog = false
    },

    /**
     * 新規行の作成
     * @param {*} type 行タイプ
     */
    async createModel (type) {

      // prefix のセット
      let prefix = ''
      switch (type) {
        case 'transfer':
          prefix = '[振]'
          break
        case 'shortageExchange':
          prefix = '[再]'
          break
        case 'exchange':
          prefix = '[交]'
          break
      }

      // 起点となった行のデータをコピー
      const base = _.cloneDeep(this.orderItem)
      const model = this.isComponentRow
      // バンドル構成品の場合
        ? {
          position: this.getNextPosition(prefix),
          lineitemText: base.lineitemText,
          productId: base.productId,
          brand: base.brand,
          price: {
            net: 0,
            gross: 0
          },
          retailPrice: base.retailPrice,
          productName: base.productName,
          qty: {
            current: 0,
            ordered: 0,
            unallocated: 0,
            pending: 0,
            preExport: 0,
            preReExport: 0,
            canceled: 0,
            exported: 0,
            shipped: 0,
            unshipped: 0,
            inshipCanceled: 0,
            transfer: 0,
            receiveShipped: 0,
            receiveFixed: 0,
            exOut: 0,
            exOutExported: 0,
            exOutShipped: 0,
            exOutUnshipped: 0,
            exOutFixed: 0,
            exIn: 0,
            exInShipped: 0,
            exInFixed: 0
          },
          preparingShipmentStartedAt: base.preparingShipmentStartedAt,
          releaseDatetime: base.releaseDatetime,
          isShipment: base.isShipment,
          point: 0,
          consumedPoint: 0,
          isBundle: 0,
          bundleItems: [],
          shipment: _.cloneDeep(this.parentItem.shipment),
          orderItemType: base.orderItemType,
          exchanges: [],
          attributes: []
        }
      // バンドル構成品でない場合
        : {
          position: this.getNextPosition(prefix),
          lineitemText: base.lineitemText,
          basePrice: {
            net: 0,
            gross: base.basePrice.gross
          },
          price: {
            net: 0,
            gross: base.price.gross
          },
          retailPrice: 0,
          taxRate: base.taxRate,
          productId: base.productId,
          brand: base.brand,
          productName: base.productName,
          colorName: base.colorName,
          sizeName: base.sizeName,
          qty: {
            current: 0,
            ordered: 0,
            unallocated: 0,
            pending: 0,
            preExport: 0,
            preReExport: 0,
            canceled: 0,
            exported: 0,
            shipped: 0,
            unshipped: 0,
            inshipCanceled: 0,
            transfer: 0,
            receiveShipped: 0,
            receiveFixed: 0,
            exOut: 0,
            exOutExported: 0,
            exOutShipped: 0,
            exOutUnshipped: 0,
            exOutFixed: 0,
            exIn: 0,
            exInShipped: 0,
            exInFixed: 0
          },
          preparingShipmentStartedAt: base.preparingShipmentStartedAt,
          releaseDatetime: base.releaseDatetime,
          isShipment: base.isShipment,
          point: 0,
          consumedPoint: 0,
          bundleItems: [],
          shipment: base.shipment,
          orderItemType: base.orderItemType,
          isSale: base.isSale,
          isGift: base.isGift,
          priceAdjustments: [],
          exchanges: [],
          attributes: []
        }

      let query = orderQuery.GRAPH_QL_QUERY_SKU_ATTR
      query = common.addGraphQlCondition(query, { skuCode: base.productId, isActive: 1 })
      const res = await common.postGraphQl(query)
      const skuData = _.get(res, 'data.data.skus.edges[0].node') || { attributes: [] }

      // 属性情報セット
      this.$_orderUtil_setOrderItemAttributes(this.displayType, model, skuData)

      return model
    },

    getNextPosition (prefix) {
      return _.chain(this.orderItems)
        .filter(val => _.startsWith(val.position, prefix))
        .map(val => Number(_.replace(val.position, prefix, '')))
        .max()
        .thru(val => `${prefix}${(val || 0) + 1}`)
        .value()
    },

    orderStatus (row) {
      return this.$_orderUtil_itemOrderStatusTag(row)
    },
    isBundleSameStatus (row) {
      // ステータスが揃っているかチェック
      if (this.isBundleHeaderRow) {
        return this.orderStatus(row).length === 1
      } else {
        return true
      }
    },

    isBundleMoveable (row, qtyKeys = []) {
      // バンドルヘッダが移動可能かチェック
      let moveable = true
      if (this.isBundleHeaderRow) {
        // バンドルフラグが 1 の場合
        if (row.isBundle === 1) {
          moveable = false
          for (const qtyKey of qtyKeys) {
            // 数量チェック
            const qty = row.moveableSetQty[qtyKey] || 0
            if (Number(qty) >= 1) {
              moveable = true
            }
          }
        }
      }
      return moveable
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/order/OrderDetailItemsToolTip.vue